import { DateTimeHelper } from '../../../../utils/dateTimeHelper';
import { TimelineEventKind } from '../models/enums/timeline-event-kind.enum';
import { TimelineEventStatus } from '../models/enums/timeline-event-status.enum';
import { TimelineEvent } from '../models/timeline-event.model';

export const isBreakMarker = (event: TimelineEvent): boolean =>
    event.kind === TimelineEventKind.BREAK_MARKER;

const startedAtUtc = (event: TimelineEvent): Date | undefined =>
    event.startedAt ? DateTimeHelper.parseUtc(event.startedAt) : undefined;

const completedAtUtc = (event: TimelineEvent): Date | undefined =>
    event.completedAt ? DateTimeHelper.parseUtc(event.completedAt) : undefined;

// [#1298 `[PO 15-Oct]`] A break lasts from the moment it fell until its timeline moves again - the Seller's when the
// catch-up reaches it, a buyer's when they come back - which is when the marker gets its completedAt. One still going
// lasts up to now. A marker written before that kept its start as its end and stays a line.
export const breakEndUtc = (marker: TimelineEvent, nowUtc: Date = new Date()): Date =>
    marker.completedAt ? DateTimeHelper.parseUtc(marker.completedAt) : nowUtc;

// [#1300 follow-up, Roman 20-Oct] While a break lasts, nothing that has not started can begin. The API plans such a
// step from today (whole days); the bar of a step not started yet begins where the open break's marker ends - now -
// at the earliest, right after it, not at the start of the day under it.
export const startNotBeforeTheOpenBreak = (
    event: TimelineEvent,
    plannedStartUtc: Date,
    events: TimelineEvent[],
    nowUtc: Date
): Date => {
    const isNotStarted = !event.startedAt && event.status === TimelineEventStatus.Upcoming;
    const isABreakOpen = events.some((row) => isBreakMarker(row) && !!row.startedAt && !row.completedAt);
    return isNotStarted && isABreakOpen && plannedStartUtc < nowUtc ? nowUtc : plannedStartUtc;
};

// [#1300 `[PO 15-Oct]`] A step the suspension cancelled takes its place in the stage just before the copy that runs it
// again - the live row of the same step; withBreakMarkers then moves it above the break's marker, with every other step
// that had started by then. A cancelled step without a copy closes the list. A step cut at two suspensions has two
// cancelled runs: the newest is placed first, so the older one lands before it and the runs stay in order.
export const withCancelledSteps = (
    steps: TimelineEvent[],
    cancelled: TimelineEvent[] | undefined
): TimelineEvent[] => {
    if (!cancelled?.length) {
        return steps;
    }

    const rows = [...steps];
    const newestFirst = [...cancelled].sort((a, b) => (b.run ?? 0) - (a.run ?? 0));
    newestFirst.forEach((step) => {
        const copyIndex = rows.findIndex(
            (row) => row.definitionId === step.definitionId && row.id !== step.id && !isBreakMarker(row)
        );
        if (copyIndex < 0) {
            rows.push(step);
            return;
        }

        rows.splice(copyIndex, 0, step);
    });

    return rows;
};

// The segment a step belongs to: before the first break it had begun by. A step that begins at the very moment of a
// break belongs after it - but a step the suspension cancelled was running at that moment, so it stays before the break
// even when it began at the same instant (the Timeline Simulator's clock stands still between actions). A step that has
// not begun follows every marker; one open or done but without dates (rows written before actual dates were stamped)
// precedes them.
const segmentOf = (step: TimelineEvent, byBreakTime: TimelineEvent[]): number => {
    const begunAt = startedAtUtc(step) ?? completedAtUtc(step);
    if (!begunAt) {
        return (step.status ?? TimelineEventStatus.Upcoming) === TimelineEventStatus.Upcoming
            ? byBreakTime.length
            : 0;
    }

    const wasCutByTheBreak = step.status === TimelineEventStatus.Cancelled;
    const firstBreakItPrecedes = byBreakTime.findIndex((marker) => {
        const brokeAt = startedAtUtc(marker)?.getTime() ?? 0;
        return wasCutByTheBreak ? begunAt.getTime() <= brokeAt : begunAt.getTime() < brokeAt;
    });
    return firstBreakItPrecedes < 0 ? byBreakTime.length : firstBreakItPrecedes;
};

// [#1298 / #1300, the API's order since #1781 D28] Break markers divide the rows into segments: what had begun by a
// break, its marker, what began after it. Inside a segment the rows keep their order - stage order, a cancelled run just
// before its copy - so every step a suspension cut sits above its marker and every copy below it, however many steps
// the suspension cut. Markers follow the order the breaks happened in.
export const withBreakMarkers = (
    steps: TimelineEvent[],
    markers: TimelineEvent[] | undefined
): TimelineEvent[] => {
    if (!markers?.length) {
        return steps;
    }

    const byBreakTime = [...markers].sort(
        (a, b) =>
            (startedAtUtc(a)?.getTime() ?? 0) - (startedAtUtc(b)?.getTime() ?? 0)
    );
    const rows: TimelineEvent[] = [];
    byBreakTime.forEach((marker, segment) => {
        rows.push(...steps.filter((step) => segmentOf(step, byBreakTime) === segment), marker);
    });
    rows.push(...steps.filter((step) => segmentOf(step, byBreakTime) === byBreakTime.length));

    return rows;
};
